
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { ServerSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { GetMobileSecuritySettingsForServerRequest } from './serverApi/requests/GetMobileSecuritySettingsForServerRequest';
import { GetMobileSecuritySettingsForSiteRequest } from './serverApi/requests/GetMobileSecuritySettingsForSiteRequest';
import { ISetMobileSecuritySettingsForServerSettingsParams, SetMobileSecuritySettingsForServerRequest } from './serverApi/requests/SetMobileSecuritySettingsForServerRequest';
import { ISetMobileSecuritySettingsForSiteSettingsParams, SetMobileSecuritySettingsForSiteRequest } from './serverApi/requests/SetMobileSecuritySettingsForSiteRequest';
import { IMobileSecuritySetting, IMobileSecuritySettings, IMobileSecuritySettingsResult } from './serverApi/types/IMobileSecuritySettings';

export const MobileSettingsSubspaceName: ServerSettingsSubspaceName = 'mobile';

export namespace MobileSecuritySettingsService {

  export function getMobileSecuritySettingsForServer(): Promise<IMobileSecuritySettings>  {
    return WebHttpClient.sendRequest(new GetMobileSecuritySettingsForServerRequest())
      .then((result: IMobileSecuritySettingsResult) => {
        return convertMobileSecuritySettingsServerResult(result);
      });
  }

  export function setMobileSecuritySettingsForServer(settings: IMobileSecuritySettings): Promise<IResult> {
    const params: ISetMobileSecuritySettingsForServerSettingsParams = {
      settings: convertMobileSecuritySettingsForServer(settings),
    };
    return WebHttpClient.sendRequest(new SetMobileSecuritySettingsForServerRequest(params));
  }

  export function getMobileSecuritySettingsForSite(siteId: string): Promise<IMobileSecuritySettings>  {
    return WebHttpClient.sendRequest(new GetMobileSecuritySettingsForSiteRequest({ siteId }))
    .then((siteResult: IMobileSecuritySettingsResult) => {
      return WebHttpClient.sendRequest(new GetMobileSecuritySettingsForServerRequest())
      .then((serverResult: IMobileSecuritySettingsResult) => {
        return sortResultBySiteThenServer(siteResult, serverResult);
      });
    });
  }

  export function setMobileSecuritySettingsForSite(siteId: string, settings: IMobileSecuritySettings): Promise<IResult> {
    const params: ISetMobileSecuritySettingsForSiteSettingsParams = {
      siteId: siteId,
      settings: convertMobileSecuritySettingsForServer(settings),
    };
    return WebHttpClient.sendRequest(new SetMobileSecuritySettingsForSiteRequest(params));
  }

  export function sortResultBySiteThenServer(siteResult: IMobileSecuritySettingsResult, serverResult: IMobileSecuritySettingsResult) {
    const convertedSiteResult = convertMobileSecuritySettingsServerResult(siteResult);
    const convertedServerResult = convertMobileSecuritySettingsServerResult(serverResult);
    let result = { ...convertedServerResult };
    Object.keys({ ...convertedSiteResult, ...convertedServerResult }).map(key => {
      const typedKey = key as keyof IMobileSecuritySettings;
      result[typedKey] = convertedSiteResult[typedKey] || convertedServerResult[typedKey];
    });
    return result;
  }

  export function convertMobileSecuritySettingServerResult(setting: IMobileSecuritySetting): IMobileSecuritySetting {
    let settingServer: IMobileSecuritySetting = {...setting,
      enabled: setting.iosConfig?.enabled || setting.androidConfig?.enabled
    };

    return settingServer;
  }

  export function convertMobileSecuritySettingsServerResult(data: IResult): IMobileSecuritySettings {
    const settings: IMobileSecuritySettings =
    {
      jailbroken_device: undefined,
      malware_detection: undefined,
      screenshot: undefined,
      max_offline: undefined,
    };
    for (const item of data as Array<IMobileSecuritySetting>) {
      const policyKey = item.key.split('.').pop();
      if (policyKey.match('jailbroken_device')) {
        settings.jailbroken_device = convertMobileSecuritySettingServerResult(item);
      } else if (policyKey.match('malware_detection')) {
        settings.malware_detection = convertMobileSecuritySettingServerResult(item);
      } else if (policyKey.match('screenshot')) {
        settings.screenshot = convertMobileSecuritySettingServerResult(item);
      } else if (policyKey.match('max_offline')) {
        settings.max_offline = convertMobileSecuritySettingServerResult(item);
      }
    }
    return settings;
  }

  export function convertMobileSecuritySettingServer(setting: IMobileSecuritySetting): IMobileSecuritySetting {
    let settingServer: IMobileSecuritySetting = {...setting, enabled: true};
    if(setting.iosConfig !== undefined) {
      settingServer.iosConfig.enabled = setting.enabled;
    }

    if(setting.androidConfig !== undefined) {
      settingServer.androidConfig.enabled = setting.enabled;
    }

    return settingServer;
  }

  export function convertMobileSecuritySettingsForServer(data: IMobileSecuritySettings): IMobileSecuritySetting[] {
    const settings: IMobileSecuritySetting[] =  new Array<IMobileSecuritySetting>();
    if (data.jailbroken_device !== undefined) {
      settings.push(convertMobileSecuritySettingServer(data.jailbroken_device));
    }
    if (data.malware_detection !== undefined) {
      settings.push(convertMobileSecuritySettingServer(data.malware_detection));
    }
    if (data.screenshot !== undefined) {
      settings.push(convertMobileSecuritySettingServer(data.screenshot));
    }
    if (data.max_offline !== undefined) {
      settings.push(convertMobileSecuritySettingServer(data.max_offline));
    }
    return settings;
  }
}
